// reCAPTCHA in the patient app (Frontend §4.6, Security §4.3; task 16). Google's `enterprise.js` is loaded lazily,
// only once P-03 is shown (never on P-01 / P-02), with the score key from `GET …/config` (#1). The client never
// decides anything: a missing token is sent as `scoreToken: null` and the server fails open (D-13).

/** Google's script (Security §4.3). `VITE_RECAPTCHA_SCRIPT_URL` replaces it for local dev and E2E only. */
export const RECAPTCHA_SCRIPT_URL = 'https://www.google.com/recaptcha/enterprise.js'
/** No score token after this long (or a script that failed to load) → `scoreToken: null` (Frontend §4.6). */
export const SCORE_TOKEN_WAIT_MS = 3000
/** How long the checkbox widget may take to appear before P-13 offers its one retry. */
export const CHECKBOX_LOAD_WAIT_MS = 8000

/** The only two actions (Security §4.3): P-03 Continue and P-09 Confirm. */
export type RecaptchaAction = 'lookup' | 'book'

export interface RecaptchaKeys {
  scoreSiteKey: string
  checkboxSiteKey: string
}

export interface CheckboxWidget {
  /** Unticks the box (after its token was sent, or when it expired). */
  reset: () => void
}

export interface CheckboxCallbacks {
  /** The box was ticked: a single-use checkbox token. */
  onToken: (token: string) => void
  /** The tick expired: the box resets and the button is disabled again. */
  onExpired: () => void
  /** The widget reported an error: treated like an expired tick. */
  onError: () => void
}

/** The patient app's view of reCAPTCHA (a fake in FR tests and stories, Testing §3). */
export interface Recaptcha {
  /** Starts loading the script (idempotent). P-03 and P-09 call it when shown. */
  load: () => void
  /** A fresh score token for `action`, right before a submit; `null` after 3 s or when the script failed. */
  executeScore: (action: RecaptchaAction) => Promise<string | null>
  /** P-13: renders the checkbox (checkbox key) into `el`; rejects when the widget can't load. */
  renderCheckbox: (el: HTMLElement, action: RecaptchaAction, callbacks: CheckboxCallbacks) => Promise<CheckboxWidget>
}

/** Makes the client for a clinic's keys (the app provides the real one; tests a fake). */
export type RecaptchaFactory = (keys: RecaptchaKeys) => Recaptcha

/** The part of `window.grecaptcha.enterprise` we use. */
interface Enterprise {
  ready: (cb: () => void) => void
  execute: (siteKey: string, options: { action: string }) => Promise<string>
  render: (el: HTMLElement, params: Record<string, unknown>) => number
  reset: (widgetId?: number) => void
}

type RecaptchaWindow = Window & { grecaptcha?: { enterprise?: Enterprise } }

function within<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('timeout'))
    }, ms)
    promise.then(
      (v) => {
        clearTimeout(timer)
        resolve(v)
      },
      (err: unknown) => {
        clearTimeout(timer)
        reject(err instanceof Error ? err : new Error('failed'))
      },
    )
  })
}

/**
 * The real client (Frontend §4.6): one `<script async src="{scriptUrl}?render={scoreSiteKey}">` per page, then
 * `grecaptcha.enterprise.execute(scoreKey, { action })` per submit and `render(el, { sitekey: checkboxKey, action,
 * callback, 'expired-callback', 'error-callback' })` for P-13. A failed load can be tried again (P-13's one retry).
 */
export function createRecaptcha(
  keys: RecaptchaKeys,
  options: { scriptUrl?: string | undefined; win?: Window; scoreWaitMs?: number; checkboxWaitMs?: number } = {},
): Recaptcha {
  const win = (options.win ?? window) as RecaptchaWindow
  const scriptUrl = options.scriptUrl ?? RECAPTCHA_SCRIPT_URL
  const scoreWaitMs = options.scoreWaitMs ?? SCORE_TOKEN_WAIT_MS
  const checkboxWaitMs = options.checkboxWaitMs ?? CHECKBOX_LOAD_WAIT_MS
  let loading: Promise<Enterprise> | null = null

  const ensure = (): Promise<Enterprise> => {
    if (loading) return loading
    const attempt = new Promise<Enterprise>((resolve, reject) => {
      const ready = () => {
        const enterprise = win.grecaptcha?.enterprise
        if (!enterprise) {
          reject(new Error('grecaptcha missing'))
          return
        }
        enterprise.ready(() => {
          resolve(enterprise)
        })
      }
      if (win.grecaptcha?.enterprise) {
        ready()
        return
      }
      const src = new URL(scriptUrl, win.location.href)
      src.searchParams.set('render', keys.scoreSiteKey)
      const script = win.document.createElement('script')
      script.src = src.toString()
      script.async = true
      script.onload = ready
      script.onerror = () => {
        script.remove()
        reject(new Error('script failed'))
      }
      win.document.head.appendChild(script)
    })
    loading = attempt
    // A failed load is forgotten, so a later call (P-13's retry) loads again.
    attempt.catch(() => {
      if (loading === attempt) loading = null
    })
    return attempt
  }

  const configured = keys.scoreSiteKey !== '' && keys.checkboxSiteKey !== ''

  return {
    load: () => {
      if (configured) ensure().catch(() => undefined)
    },
    executeScore: async (action) => {
      if (!configured) return null
      try {
        const enterprise = await within(ensure(), scoreWaitMs)
        const token = await within(enterprise.execute(keys.scoreSiteKey, { action }), scoreWaitMs)
        return typeof token === 'string' && token !== '' ? token : null
      } catch {
        return null
      }
    },
    renderCheckbox: async (el, action, callbacks) => {
      if (!configured) throw new Error('reCAPTCHA is not configured')
      const enterprise = await within(ensure(), checkboxWaitMs)
      const id = enterprise.render(el, {
        sitekey: keys.checkboxSiteKey,
        action,
        callback: callbacks.onToken,
        'expired-callback': callbacks.onExpired,
        'error-callback': callbacks.onError,
      })
      return {
        reset: () => {
          enterprise.reset(id)
        },
      }
    },
  }
}
